// The founder's pulse (Slice 6; slice-plan.md:83-92; data-model.md:44 metrics_snapshots; ADR-0023).
//
// The closed seven founder metrics the pulse reads, plus the passive churn/anomaly signals that
// route to shift intake (D45; slice-plan.md:90). D27 applies here too: a metric is a value in its
// own unit (a count, a percentage, a duration), never a cost or token figure - the customer-facing
// `PulseView` is `z.strictObject` so a smuggled cost/token key is a PARSE ERROR, mirroring the
// `BuildStepView` precedent (model/build.ts:121-134).
import { z } from "zod";
import { AccountId, AppId, MetricSnapshotId } from "../ids.js";
import { NonEmptyString, Timestamp } from "./common.js";
/** The closed seven founder metrics (data-model.md:44). Widen only through an ADR. */
export const MetricName = z.enum([
    "active_users",
    "signups",
    "retention",
    "revenue",
    "errors",
    "p95",
    "uptime",
]);
/**
 * One captured metric row (data-model.md:44 metrics_snapshots). `value` is the metric's own unit
 * (a count, a percentage, a duration) - NEVER a cost or token figure. `z.number()` already rejects
 * NaN/Infinity in zod v4 (the non-finite guard, app-engineering §2, is built into the base
 * schema - no separate `.finite()` call, which is a deprecated no-op in v4).
 *
 * `strictObject`: though this row schema is not itself a tenant response (it feeds the projection),
 * making it strict is the same loud-reject posture as `PulseMetricPoint`/`PulseView` below - a raw
 * `metrics_snapshots` row with an extra cost/token column fails the boundary parse rather than
 * carrying the key silently. `rowToMetricSnapshot` (pulse.service.ts) already copies only the named
 * fields, so this never rejects a real row; it is a second wall, not a new constraint on the happy path.
 */
export const MetricSnapshot = z.strictObject({
    id: MetricSnapshotId,
    accountId: AccountId,
    appId: AppId,
    metric: MetricName,
    value: z.number(),
    capturedAt: Timestamp,
});
/**
 * ONE metric point in the customer-facing pulse read (D27 wall). `strictObject` rejects a
 * smuggled cost/token key so the pulse view cannot carry a per-action figure alongside a metric.
 * Drops `id`/`accountId` (the row identity and tenant are not the founder's concern here).
 */
export const PulseMetricPoint = z.strictObject({
    metric: MetricName,
    value: z.number(),
    capturedAt: Timestamp,
});
/**
 * THE PULSE (slice-plan.md:86-90; D27 wall). The seven founder metrics for one app - no cost
 * surface. `strictObject` on both the view and every metric point makes a smuggled cost/token
 * field a parse error at any depth.
 */
export const PulseView = z.strictObject({
    appId: AppId,
    metrics: z.array(PulseMetricPoint),
});
/**
 * The passive churn signals that route to shift intake (D45; slice-plan.md:90: "passive churn
 * signals (stalled onboarding, repeated errors, feature drop-off) route to intake"). Widen only
 * through an ADR.
 */
export const ChurnSignalKind = z.enum([
    "stalled_onboarding",
    "repeated_error",
    "feature_dropoff",
    "time_to_value_creep",
]);
/**
 * An anomaly/churn signal the pulse detects for one app, routed to shift intake as passive signal
 * (slice-plan.md:86,90; ADR-0023 decision 8, `pulse.real_anomaly`). `strictObject` so a detected
 * signal cannot carry a smuggled cost/token field either.
 */
export const AnomalySignal = z.strictObject({
    appId: AppId,
    kind: ChurnSignalKind,
    detectedAt: Timestamp,
    summary: NonEmptyString,
});
//# sourceMappingURL=pulse.js.map